/**
 * First-visit tutorial modal — 4 steps with gradient banner per step.
 * Controlled component: caller decides when to show; component handles step
 * navigation and persists the seen flag when finished or skipped.
 */
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { currencyGlyph } from '@/lib/money/index.js';
import { useDisplayMoney } from '@/components/money/MoneyProvider.js';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { markTutorialSeen } from '@/features/promo/tutorial-state.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Brand ramp (--art-*, derived from --primary/--accent) so the tutorial banners
// follow the tenant theme.
const GRADIENTS = [
  'var(--art-1)',
  'var(--art-2)',
  'var(--art-4)',
  'var(--art-6)',
];

/* Spec-approved step illustrations — emoji intentional per design brief */
const STEP_ICONS = ['\u{1F381}', '\u{1F4B0}', '\u{1F3AF}', '✨'];

export function PromoTutorial({ open, onClose }: Props) {
  const { t } = useTranslation('promo');
  const [step, setStep] = useState(1);

  const finish = () => {
    markTutorialSeen();
    onClose();
    setStep(1);
  };

  const skip = () => finish();

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) skip();
      }}
    >
      <DialogContent hideClose className="max-w-[320px] gap-0 overflow-hidden p-0">
        {/* Gradient banner */}
        <div
          className="p-5 pb-4 text-white relative"
          style={{ background: GRADIENTS[step - 1] }}
        >
          <div className="flex items-center justify-between">
            <span className="px-2.5 py-0.5 rounded-xl text-[10px] font-bold uppercase tracking-wide bg-white/20">
              {step} / 4
            </span>
            <button onClick={skip} className="text-white/70 text-[11px]">
              {t('tutorial.actions.skip')}
            </button>
          </div>
          <div
            className="text-[36px] mt-3"
            style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.35))' }}
          >
            {STEP_ICONS[step - 1]}
          </div>
          <DialogTitle className="text-[16px] font-extrabold mt-2 leading-tight text-white">
            {t(`tutorial.step${step}.title`)}
          </DialogTitle>
          <div className="text-[11px] opacity-90 mt-1">
            {t(`tutorial.step${step}.body`)}
          </div>
        </div>

        {/* Step body + navigation */}
        <div className="bg-[var(--bg-raised)] p-4 text-[var(--text-primary)] text-[11.5px] leading-relaxed">
          <StepBody step={step} t={t} />

          {/* Progress dots */}
          <div className="flex gap-1.5 justify-center my-3">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={[
                  'h-[5px] rounded-full transition-all',
                  n === step
                    ? 'w-6 bg-white shadow-[0_0_8px_rgba(255,255,255,.4)]'
                    : 'w-4 bg-[var(--bg-elevated)]',
                ].join(' ')}
              />
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex-1 rounded-lg py-2.5 font-bold text-[12px] bg-[var(--bg-elevated)] text-[var(--text-secondary)] disabled:opacity-40"
            >
              {t('tutorial.actions.back')}
            </button>
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex-[2] rounded-lg py-2.5 font-bold text-[12px] text-[#1f2937] bg-[#fbbf24] shadow-[0_4px_12px_rgba(251,191,36,.35)]"
              >
                {t('tutorial.actions.next')}
              </button>
            ) : (
              <button
                onClick={finish}
                className="flex-[2] rounded-lg py-2.5 font-bold text-[12px] text-white bg-[var(--success)] shadow-[0_4px_12px_rgba(16,185,129,.4)]"
              >
                {t('tutorial.actions.finish')}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StepBody({ step, t }: { step: number; t: TFunction }) {
  // The worked example is illustrative, but the SYMBOL is not: on a JPY brand a
  // tutorial denominated in baht is simply wrong, and this is the screen that
  // teaches a player how bonuses work (WEB-25).
  const g = currencyGlyph(useDisplayMoney().currency);
  switch (step) {
    case 1:
      return (
        <ul className="pl-4 list-disc text-[var(--text-secondary)] space-y-0.5">
          <li>{t('tutorial.step1.bullet1')}</li>
          <li>{t('tutorial.step1.bullet2')}</li>
          <li>{t('tutorial.step1.bullet3')}</li>
          <li>{t('tutorial.step1.bullet4')}</li>
          <li>{t('tutorial.step1.bullet5')}</li>
        </ul>
      );
    case 2:
      return (
        <div className="bg-[var(--bg-base)] rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-[var(--success)] font-bold">
              {t('tutorial.step2.labelCash', { amount: `${g}200` })}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-[#fbbf24] font-bold">
              {t('tutorial.step2.labelBonus', { amount: `${g}50` })}
            </span>
          </div>
        </div>
      );
    case 3:
      return (
        <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
          <div className="text-[11px] text-[var(--text-secondary)]">
            {t('tutorial.step3.example', { bonus: `${g}50`, mult: 5, target: `${g}250` })}
          </div>
          <div className="text-[20px] font-extrabold text-[#fbbf24] mt-1">
            {g}250
          </div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1">
            {t('tutorial.step3.footnote')}
          </div>
        </div>
      );
    case 4:
      return (
        <div className="space-y-2">
          <div className="bg-[var(--bg-base)] rounded-lg p-2.5 flex items-center justify-between text-[11px]">
            <span className="text-[#fbbf24]">{g}50 bonus</span>
            <span className="text-[var(--text-muted)]">&#x2192;</span>
            <span className="text-[var(--success)]">{g}50 cash &#x2713;</span>
          </div>
          <div className="flex items-start gap-1.5 text-[11px] text-[var(--text-secondary)]">
            <AlertTriangle aria-hidden size={14} className="shrink-0 mt-px text-[#fbbf24]" />
            <span>{t('tutorial.step4.warning')}</span>
          </div>
        </div>
      );
    default:
      return null;
  }
}
